import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useTimer } from '../../util/timer.js';
import { useEnterExitAnimation } from '../../util/useEnterExitAnimation.js';
import { useEventListener } from '../../util/useEventListener.js';
import { Box } from '../Box/Box.js';
import { actionElement, iconElement, titleElement, descriptionElement } from './BaseNotification.js';
import { getTokens } from './Notification.mappers.js';

var _excluded = ["visible", "intent", "onNotificationClose", "title", "description", "actions", "leadingIcon", "zIndex"];
var getDefaultIcon = function getDefaultIcon(intent) {
  switch (intent) {
    case 'positive':
      return 'fill.symbol.circle-check';
    case 'warning':
      return 'fill.symbol.circle-exclamation';
    case 'error':
      return 'fill.symbol.triangle-exclamation';
    default:
      return 'fill.symbol.circle-info';
  }
};
var WORDS_120 = 120;
var DEFAULT_VISIBLE_DURATION_MS = 5000;
var ONE_SECOND_MS = 1000;
var getAccessibilityProps = function getAccessibilityProps(actions, intent) {
  return {
    role: 'alert',
    'aria-live': actions || intent === 'error' ? 'assertive' : 'polite'
  };
};

// For every 120 words, add 1 second to the notification visible duration
var getVisibleDuration = function getVisibleDuration(description) {
  var _description$split$le;
  var descriptionWords = (_description$split$le = description === null || description === void 0 ? void 0 : description.split(' ').length) !== null && _description$split$le !== void 0 ? _description$split$le : 0;
  return DEFAULT_VISIBLE_DURATION_MS + Math.floor(descriptionWords / WORDS_120) * ONE_SECOND_MS;
};

/**
 * Top notification is a type of alert that appears at the top of the user interface. It is designed to immediately capture the user's attention. While this notification is present, other interactions on the screen are blocked. It remains in place until it's dismissed.
 */
var TopNotification = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var visible = _ref.visible,
    intent = _ref.intent,
    onNotificationClose = _ref.onNotificationClose,
    title = _ref.title,
    description = _ref.description,
    actions = _ref.actions,
    leadingIcon = _ref.leadingIcon,
    zIndex = _ref.zIndex,
    rest = _objectWithoutProperties(_ref, _excluded);
  var closeNotification = React.useCallback(function () {
    onNotificationClose();
  }, [onNotificationClose]);
  var visibleDuration = getVisibleDuration(description);
  var timerCallBack = React.useCallback(function () {
    if (!actions && visible) {
      closeNotification();
    }
  }, [actions, closeNotification, visible]);
  var closeNotificationTimer = useTimer({
    delay: visibleDuration,
    callback: timerCallBack
  }, [visible]);
  var _useEnterExitAnimatio = useEnterExitAnimation({
      transition: 'all 0.3s ease-in-out',
      initialProps: {
        transform: 'translateY(-100%)'
      },
      enterProps: {
        transform: 'translateY(0)'
      },
      animationOn: visible,
      animationFallBackTimeMs: 500
    }),
    transitionProps = _useEnterExitAnimatio.transitionProps,
    shouldRender = _useEnterExitAnimatio.shouldRender;
  useEventListener({
    eventName: 'click',
    handler: function handler() {
      closeNotification();
      closeNotificationTimer.destroy();
    },
    attached: visible,
    capture: true
  });
  var clickNotification = function clickNotification(event) {
    event.stopPropagation();
  };
  var _React$useMemo = React.useMemo(function () {
      return getTokens(intent);
    }, [intent]),
    background = _React$useMemo.background,
    text = _React$useMemo.text;
  var icon = leadingIcon !== null && leadingIcon !== void 0 ? leadingIcon : getDefaultIcon(intent);
  var actionsElement = React.useMemo(function () {
    return actions && /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      alignItems: "baseline",
      gap: "12"
    }, actions.map(function (action) {
      return actionElement(action);
    }));
  }, [actions]);
  var accessibilityProps = getAccessibilityProps(actions, intent);
  return shouldRender ?
  /*#__PURE__*/
  // eslint-disable-next-line @agoda/agoda-a11y/no-incorrect-props -- @agoda/agoda-a11y/no-incorrect-props[60a20907-0d72-4e1d-9242-a4d0b6c2dd12]
  React.createElement(Box, _extends({
    ref: ref,
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "fit-content",
    p: "16",
    backgroundColor: background,
    textColor: text,
    boxShadow: "bottom-m",
    onClick: clickNotification,
    display: "flex",
    alignItems: "stretch",
    overflow: "hidden",
    zIndex: zIndex
  }, accessibilityProps, transitionProps, rest), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    safeArea: {
      top: true
    }
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "row"
  }, iconElement(icon, text), /*#__PURE__*/React.createElement(Box, {
    pt: "2"
  }, titleElement(title, description), descriptionElement(description))), actionsElement)) : null;
});
TopNotification.displayName = 'TopNotification';

export { TopNotification };
//# sourceMappingURL=TopNotification.js.map
